// Bulk uploader. Lives in a context above the routes so a 50-file batch
// survives navigation. Files go straight to S3 via presigned PUT
// (sequential, with progress); hashing happens client-side so duplicates
// are refused before any bytes move. Once bytes are in S3 everything else
// is server-side, so the detective can close the tab — the server's
// reconcile step picks up anything the client never confirmed.
import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import UploadTrayView from "../common/UploadTrayView";
import { api, audioDurationSeconds, sha256File } from "./api";

export type UploadItem = {
  id: string;
  investigationId: string;
  file: File;
  status: "hashing" | "queued" | "uploading" | "done" | "error" | "duplicate" | "skipped";
  progress: number;
  error?: string;
  kind?: string;
};

type Ctx = {
  items: UploadItem[];
  enqueue: (investigationId: string, files: File[]) => Promise<void>;
  clearFinished: () => void;
  active: boolean;
};

const UploadContext = createContext<Ctx | null>(null);
export const useUploader = () => {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error("useUploader must be inside UploadProvider");
  return ctx;
};

const PRIORITY_FIRST_N = 10;

export const UploadProvider: React.FC<React.PropsWithChildren<{ onBatchComplete?: () => void }>> = ({ children, onBatchComplete }) => {
  const [items, setItems] = useState<UploadItem[]>([]);
  const runningRef = useRef(false);
  const queueRef = useRef<UploadItem[]>([]);

  const update = useCallback((id: string, patch: Partial<UploadItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  const pump = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      while (queueRef.current.length) {
        const batch = queueRef.current.splice(0, queueRef.current.length);
        const investigationId = batch[0].investigationId;
        const sameInv = batch.filter((b) => b.investigationId === investigationId);
        const rest = batch.filter((b) => b.investigationId !== investigationId);
        queueRef.current.unshift(...rest);

        // Hash, then register the whole batch in one call.
        const metas: { item: UploadItem; sha256?: string }[] = [];
        for (const item of sameInv) {
          update(item.id, { status: "hashing" });
          const sha256 = await sha256File(item.file);
          metas.push({ item, sha256 });
          update(item.id, { status: "queued" });
        }
        let prepared: Awaited<ReturnType<typeof api.prepareUploads>>;
        try {
          prepared = await api.prepareUploads(
            investigationId,
            metas.map(({ item, sha256 }) => ({ fileName: item.file.name, fileType: item.file.type || "application/octet-stream", size: item.file.size, sha256 }))
          );
        } catch (error: any) {
          const msg = error?.response?.data?.error === "PRIVILEGE_ATTESTATION_REQUIRED" ? "Privilege attestation required" : error?.response?.data?.error || "Upload could not start";
          for (const { item } of metas) update(item.id, { status: "error", error: msg });
          toast.error(msg);
          continue;
        }

        let index = 0;
        for (let i = 0; i < metas.length; i++) {
          const { item } = metas[i];
          const p = prepared[i];
          if (!p?.uploadURL || !p.fileId) {
            update(item.id, { status: p?.duplicateOf ? "duplicate" : "skipped", error: p?.error });
            continue;
          }
          update(item.id, { status: "uploading", progress: 0, kind: p.kind });
          try {
            await axios.put(p.uploadURL, item.file, {
              headers: { "Content-Type": item.file.type || "application/octet-stream" },
              onUploadProgress: (e) => update(item.id, { progress: e.total ? Math.round((e.loaded * 100) / e.total) : 0 }),
            });
            const durationSeconds = p.kind === "jail_call" ? await audioDurationSeconds(item.file) : undefined;
            await api.confirmUpload(investigationId, p.fileId, { priority: index < PRIORITY_FIRST_N ? 0 : 10, durationSeconds });
            index++;
            update(item.id, { status: "done", progress: 100 });
          } catch (error: any) {
            update(item.id, { status: "error", error: error?.response?.data?.error || "Upload failed" });
          }
        }
        onBatchComplete?.();
      }
    } finally {
      runningRef.current = false;
    }
  }, [update, onBatchComplete]);

  const enqueue = useCallback(
    async (investigationId: string, files: File[]) => {
      const newItems: UploadItem[] = files.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        investigationId,
        file,
        status: "queued",
        progress: 0,
      }));
      setItems((prev) => [...prev, ...newItems]);
      queueRef.current.push(...newItems);
      void pump();
    },
    [pump]
  );

  const active = items.some((i) => ["hashing", "queued", "uploading"].includes(i.status));

  // Warn before leaving only while bytes are still moving; once uploads
  // finish, processing continues server-side.
  useEffect(() => {
    if (!active) return;
    const h = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [active]);

  const clearFinished = useCallback(() => setItems((prev) => prev.filter((i) => ["hashing", "queued", "uploading"].includes(i.status))), []);

  const value = useMemo(() => ({ items, enqueue, clearFinished, active }), [items, enqueue, clearFinished, active]);
  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
};

export const UploadTray: React.FC = () => {
  const { items, clearFinished } = useUploader();
  return (
    <UploadTrayView
      items={items.map((i) => ({ id: i.id, name: i.file.name, status: i.status, progress: i.progress, error: i.error, doneLabel: "queued" }))}
      onClearFinished={clearFinished}
      activeHint="Keep this tab open until uploads finish. Analysis continues on its own."
      finishedHint="Analysis is running on the server. You can leave this page."
    />
  );
};

export const ACCEPT =
  ".wav,.mp3,.m4a,.mp4,.mov,.webm,.mkv,.avi,.m4v,video/*,.flac,.ogg,.amr,.aac,.pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.tsv,.jpg,.jpeg,.png,.webp,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,image/*";
